import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '../context/AuthContext';
import { useActiveFamily } from '../context/ActiveFamilyContext';
import { useMemberRole } from './useMemberRole';
import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { getNativeDeviceId, getNativePushToken, registerOrUpdateDevice } from '../services/deviceService';

const HOME_DEVICE_KEY = 'call_home_device_enabled';
const PRIMARY_DEVICE_KEY = 'call_primary_device_confirmed';
const DEVICE_CONFIGURED_KEY = 'call_device_configured'; // Nueva key para detectar si ya fue configurado
const PRIMARY_DEVICE_LAST_CONFIRM_KEY = 'call_primary_device_last_confirmed_at';

export const useDeviceRegistry = () => {
  const { user } = useAuth();
  const { activeFamily } = useActiveFamily();
  const { memberId } = useMemberRole();
  const [familyHomeDeviceId, setFamilyHomeDeviceId] = useState<string | null>(null);
  const [familyHomeDeviceMeta, setFamilyHomeDeviceMeta] = useState<{ userId?: string | null; memberId?: string | null } | null>(null);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [needsPrimaryDeviceConfirm, setNeedsPrimaryDeviceConfirm] = useState(false);
  const [needsDeviceTypeSelection, setNeedsDeviceTypeSelection] = useState(false); // Nuevo estado
  const [deviceFamilyCheckDone, setDeviceFamilyCheckDone] = useState(false);
  const [isKnownDeviceForFamily, setIsKnownDeviceForFamily] = useState<boolean | null>(null);
  const [isHomeDevice, setIsHomeDevice] = useState<boolean>(() => {
    try {
      return localStorage.getItem(HOME_DEVICE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [isPrimaryForUser, setIsPrimaryForUser] = useState<boolean>(() => {
    try {
      return localStorage.getItem(PRIMARY_DEVICE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    const run = async () => {
      if (!isNative) return;
      if (!user?.uid) return;
      // Esperar a tener familia activa para poder validar si el device pertenece a esa familia.
      // Si corremos antes (cold start), podemos marcar erróneamente como "no reconocido" y
      // disparar el prompt en cada arranque.
      if (!activeFamily?.id) return;

      setDeviceFamilyCheckDone(false);
      setIsKnownDeviceForFamily(null);

      const currentDeviceId = await getNativeDeviceId();
      if (!currentDeviceId) return;

      try {
        // ✅ Verificar el doc del device por ID nativo.
        // Esto permite reconocer un dispositivo ya registrado para la familia,
        // incluso si fue configurado por otro admin/usuario.
        const deviceSnap = await getDoc(doc(db, 'devices', currentDeviceId));
        const deviceData: any = deviceSnap.exists() ? deviceSnap.data() : null;

        const sameFamily = Boolean(activeFamily?.id && deviceData?.familyId && deviceData.familyId === activeFamily.id);
        const knownForFamily = deviceSnap.exists() && sameFamily;

        setIsKnownDeviceForFamily(knownForFamily);
        setDeviceFamilyCheckDone(true);

        if (!knownForFamily) {
          console.log('📱 Dispositivo no reconocido para esta familia - mostrando prompt de configuración', {
            currentDeviceId,
            familyId: activeFamily?.id,
            foundFamilyId: deviceData?.familyId,
          });

          try {
            localStorage.setItem(DEVICE_CONFIGURED_KEY, '0');
            localStorage.setItem(HOME_DEVICE_KEY, '0');
            localStorage.setItem(PRIMARY_DEVICE_KEY, '0');
            localStorage.removeItem(PRIMARY_DEVICE_LAST_CONFIRM_KEY);
          } catch {}

          // No bloquear pantallas de llamadas con un modal no-dismissible.
          // Si estamos dentro de /calls, postergar el prompt hasta que el usuario vuelva al flujo normal.
          const isOnCallsRoute = (() => {
            try {
              return window.location.pathname.startsWith('/calls');
            } catch {
              return false;
            }
          })();

          if (!isOnCallsRoute) {
            setNeedsDeviceTypeSelection(true);
          }
          setNeedsPrimaryDeviceConfirm(false);
          return;
        }

        // Si es un device conocido para esta familia, marcar como configurado.
        try {
          localStorage.setItem(DEVICE_CONFIGURED_KEY, '1');
        } catch {}

        // Sync de flag isHomeDevice a partir de Firestore (la verdad está en el doc)
        if (typeof deviceData?.isHomeDevice === 'boolean') {
          try {
            localStorage.setItem(HOME_DEVICE_KEY, deviceData.isHomeDevice ? '1' : '0');
          } catch {}
          setIsHomeDevice(!!deviceData.isHomeDevice);
        }
      } catch (error) {
        console.warn('Error verificando dispositivos conocidos:', error);
        setDeviceFamilyCheckDone(true);
        setIsKnownDeviceForFamily(null);
      }
    };

    void run();
  }, [isNative, user?.uid, activeFamily?.id]);

  // Obtener el device configurado como "Dispositivo de Casa" para la familia (si existe)
  useEffect(() => {
    const run = async () => {
      if (!activeFamily?.id) {
        setFamilyHomeDeviceId(null);
        setFamilyHomeDeviceMeta(null);
        return;
      }

      try {
        const snap = await getDocs(
          query(
            collection(db, 'devices'),
            where('familyId', '==', activeFamily.id),
            where('isHomeDevice', '==', true),
            limit(1)
          )
        );

        if (snap.empty) {
          setFamilyHomeDeviceId(null);
          setFamilyHomeDeviceMeta(null);
          return;
        }

        const d = snap.docs[0];
        const data: any = d.data();
        setFamilyHomeDeviceId(d.id);
        setFamilyHomeDeviceMeta({
          userId: data?.userId || null,
          memberId: data?.memberId || null,
        });
      } catch (e) {
        console.warn('Error leyendo dispositivo de casa de la familia:', e);
        setFamilyHomeDeviceId(null);
        setFamilyHomeDeviceMeta(null);
      }
    };

    void run();
  }, [activeFamily?.id]);

  useEffect(() => {
    if (!isNative) return;
    if (!user?.uid) return;

    // Importante:
    // - No depender de localStorage para decidir si el device es nuevo.
    // - La fuente de verdad es Firestore: si el deviceId ya existe para la familia,
    //   no se debe volver a mostrar el prompt.
    // - Solo mostramos confirmación primary para devices personales ya conocidos.

    if (!deviceFamilyCheckDone) return;
    if (isKnownDeviceForFamily !== true) return;

    // Si es device de casa, nunca pedir confirmación primary.
    if (isHomeDevice) {
      setNeedsPrimaryDeviceConfirm(false);
      return;
    }

    if (!isPrimaryForUser) {
      // Dispositivo conocido pero sin confirmar como primary.
      // Solo volver a pedir confirmación si pasó mucho tiempo sin confirmarse.
      let shouldPrompt = true;
      try {
        const last = localStorage.getItem(PRIMARY_DEVICE_LAST_CONFIRM_KEY);
        const lastMs = last ? Number(last) : 0;
        const days30 = 30 * 24 * 60 * 60 * 1000;
        if (lastMs && Date.now() - lastMs < days30) {
          shouldPrompt = false;
        }
      } catch {}
      if (shouldPrompt) setNeedsPrimaryDeviceConfirm(true);
    }
  }, [isNative, user?.uid, isPrimaryForUser, isHomeDevice, deviceFamilyCheckDone, isKnownDeviceForFamily]);

  const setHomeDeviceEnabled = (value: boolean) => {
    try {
      localStorage.setItem(HOME_DEVICE_KEY, value ? '1' : '0');
      localStorage.setItem(DEVICE_CONFIGURED_KEY, '1'); // Marcar como configurado

      // Un dispositivo no debe ser simultáneamente "casa" y "principal"
      if (value) {
        localStorage.setItem(PRIMARY_DEVICE_KEY, '0');
      }
    } catch {}
    setIsHomeDevice(value);

    if (value) {
      setIsPrimaryForUser(false);
      setNeedsPrimaryDeviceConfirm(false);
    }
  };

  // Función para configurar como dispositivo de casa (desde prompt)
  const configureAsHomeDevice = async () => {
    try {
      localStorage.setItem(HOME_DEVICE_KEY, '1');
      localStorage.setItem(PRIMARY_DEVICE_KEY, '0');
      localStorage.setItem(DEVICE_CONFIGURED_KEY, '1');
      setIsHomeDevice(true);
      setIsPrimaryForUser(false);
      setNeedsDeviceTypeSelection(false);
      console.log('✅ Dispositivo configurado como dispositivo de casa');
    } catch (error) {
      console.error('Error configurando dispositivo de casa:', error);
    }
  };

  // Función para configurar como dispositivo personal (desde prompt)
  const configureAsMobileDevice = async () => {
    try {
      localStorage.setItem(HOME_DEVICE_KEY, '0');
      localStorage.setItem(PRIMARY_DEVICE_KEY, '1');
      localStorage.setItem(DEVICE_CONFIGURED_KEY, '1');
      setIsHomeDevice(false);
      setIsPrimaryForUser(true);
      setNeedsDeviceTypeSelection(false);
      console.log('✅ Dispositivo configurado como dispositivo personal');
    } catch (error) {
      console.error('Error configurando dispositivo personal:', error);
    }
  };

  useEffect(() => {
    const run = async () => {
      if (!isNative) return;
      if (!user?.uid) return;

      const pushToken = await getNativePushToken();

      // Fallback: si useMemberRole no resolvió memberId, usar el active_member_id.
      // Esto es clave para que el sistema de llamadas detecte devices por miembro.
      let resolvedMemberId = memberId || null;
      if (!resolvedMemberId) {
        try {
          resolvedMemberId = localStorage.getItem('active_member_id');
        } catch {
          resolvedMemberId = null;
        }
      }

      const res = await registerOrUpdateDevice({
        userId: user.uid,
        memberId: resolvedMemberId,
        familyId: activeFamily?.id || null,
        pushToken,
        isHomeDevice,
        isPrimaryForUser,
      });

      setDeviceId(res.deviceId);
    };

    run();
  }, [
    isNative,
    user?.uid,
    memberId,
    activeFamily?.id,
    isHomeDevice,
    isPrimaryForUser,
  ]);

  // Computed: si ya confirmó primary device
  const isPrimaryDevice = localStorage.getItem(PRIMARY_DEVICE_KEY) === '1';

  // Setter para cambiar primary device desde UI
  const setPrimaryDeviceConfirmed = (value: boolean) => {
    try {
      localStorage.setItem(PRIMARY_DEVICE_KEY, value ? '1' : '0');
      localStorage.setItem(PRIMARY_DEVICE_LAST_CONFIRM_KEY, String(Date.now()));

      // Un dispositivo no debe ser simultáneamente "principal" y "casa"
      if (value) {
        localStorage.setItem(HOME_DEVICE_KEY, '0');
      }
    } catch {}
    setIsPrimaryForUser(value);
    setNeedsPrimaryDeviceConfirm(false);

    if (value) {
      setIsHomeDevice(false);
    }
  };

  // Platform info
  const platform = Capacitor.getPlatform();

  return {
    deviceId,
    isNative,
    isHomeDevice,
    setHomeDeviceEnabled,
    needsPrimaryDeviceConfirm,
    needsDeviceTypeSelection,
    isPrimaryDevice,
    setPrimaryDeviceConfirmed,
    configureAsHomeDevice,
    configureAsMobileDevice,
    platform,
    familyHomeDeviceId,
    familyHomeDeviceMeta,
  };
};
